<template>
  <div class="enhanced-markdown-renderer">
    <div v-html="processedHtml" class="markdown-content"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from 'vue'
import { marked } from 'marked'
import { parseMarkdownDirectory, type DirectoryItem } from '@/utils/directoryParser'

interface Props {
  content: string
  enableSyntaxHighlighting?: boolean
  enableCollapsibleTrees?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  enableSyntaxHighlighting: true,
  enableCollapsibleTrees: true
})

const processedHtml = ref('')

// Load Prism.js dynamically for syntax highlighting
const loadPrism = async () => {
  if (!props.enableSyntaxHighlighting) return

  try {
    // Load Prism.js core
    const Prism = await import('prismjs')
    
    // Load additional language support
    // @ts-ignore - Prism.js components don't have type definitions
    await import('prismjs/components/prism-javascript')
    // @ts-ignore
    await import('prismjs/components/prism-typescript')
    // @ts-ignore
    await import('prismjs/components/prism-python')
    // @ts-ignore
    await import('prismjs/components/prism-json')
    // @ts-ignore
    await import('prismjs/components/prism-bash')
    // @ts-ignore
    await import('prismjs/components/prism-sql')
    // @ts-ignore
    await import('prismjs/components/prism-yaml')
    // @ts-ignore
    await import('prismjs/components/prism-docker')
    
    // Use local VS Dark theme styling (defined in CSS section below)

    return Prism.default || Prism
  } catch {
    return null
  }
}

// Custom renderer for marked
const setupMarkedRenderer = () => {
  const renderer = new marked.Renderer()

  // Custom code block renderer
  renderer.code = ({ text, lang }: { text: string, lang?: string }) => {
    const language = lang || 'text'
    
    // Special handling for tree language
    if (language === 'tree') {
      return generateCollapsibleTree(text)
    }
    
    // Generate unique ID for copy functionality
    const codeId = `code-${Math.random().toString(36).substr(2, 9)}`
    
    return `
      <div class="code-block-container">
        <div class="code-block-header">
          <span class="code-language">${language}</span>
          <button class="copy-button" onclick="copyCode('${codeId}')" title="Copy code">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="m5 15-4-4 4-4"></path>
            </svg>
          </button>
        </div>
        <pre class="language-${language}"><code id="${codeId}" class="language-${language}">${escapeHtml(text)}</code></pre>
      </div>
    `
  }

  // Custom blockquote renderer for enhanced content types
  renderer.blockquote = ({ tokens }: { tokens: any[] }) => {
    // Convert tokens back to text for processing
    const content = tokens.map((token: any) => token.raw || token.text || '').join('').trim()
    
    // Check for checklist
    if (isChecklist(content)) {
      return generateChecklist(content)
    }
    
    // Check for config file
    if (isConfigFile(content)) {
      return generateConfigFile(content)
    }
    
    // Regular blockquote with smart styling
    return `<blockquote class="smart-blockquote">${content}</blockquote>`
  }

  return renderer
}

// Check if content is a checklist
const isChecklist = (content: string): boolean => {
  const lines = content.split('\n')
  return lines.some(line => /^\s*[-*]\s*\[([ x])\]/.test(line))
}

// Check if content looks like a configuration file
const isConfigFile = (content: string): boolean => {
  const configPatterns = [
    /^\s*[\w-]+\s*[:=]/m,           // key: value or key = value
    /^\s*\[[\w-]+\]/m,              // [section]
    /^\s*#.*config/im,              // comments mentioning config
    /^\s*\/\*.*config.*\*\//im,     // /* config comment */
    /^\s*<!--.*config.*-->/im,      // <!-- config comment -->
  ]
  
  return configPatterns.some(pattern => pattern.test(content))
}

// Generate collapsible tree from tree syntax
const generateCollapsibleTree = (treeContent: string): string => {
  if (!props.enableCollapsibleTrees) {
    return `<pre class="tree-display">${escapeHtml(treeContent)}</pre>`
  }

  try {
    // Extract only the tree content from the code block
    const treeMatch = treeContent.match(/```tree\s*\n([\s\S]*?)\n```/)
    if (treeMatch) {
      const actualTreeContent = treeMatch[1]
      const treeItems = parseMarkdownDirectory(actualTreeContent)
      return generateDirectoryTreeHTML(treeItems)
    } else {
      // No tree code block found, treat as raw tree content
      const treeItems = parseMarkdownDirectory(treeContent)
      return generateDirectoryTreeHTML(treeItems)
    }
  } catch {
    return `<pre class="tree-display">${escapeHtml(treeContent)}</pre>`
  }
}

// Generate HTML for directory tree
const generateDirectoryTreeHTML = (items: DirectoryItem[], level = 0): string => {
  const treeItemsHtml = items.map(item => {
    const indent = level * 20
    const isDirectory = item.type === 'directory'
    const hasChildren = item.children && item.children.length > 0
    const childrenHtml = hasChildren ? generateDirectoryTreeHTML(item.children!, level + 1) : ''
    
    // Default to collapsed state for directories
    const isExpanded = item.isExpanded || false
    
    return `
      <div class="tree-item" style="margin-left: ${indent}px;">
        <div class="tree-node ${isDirectory ? 'directory' : 'file'}">
          ${isDirectory ? 
            `<span class="tree-icon directory-icon ${isExpanded ? 'expanded' : 'collapsed'}" onclick="toggleDirectory('${item.id}')" style="cursor: pointer;">
               ${isExpanded ? '📂' : '📁'}
             </span>` : 
            '<span class="tree-icon file-icon">📄</span>'
          }
          <span class="tree-name">${escapeHtml(item.name)}</span>
          ${item.description ? `<span class="tree-description"># ${escapeHtml(item.description)}</span>` : ''}
        </div>
        ${hasChildren ? `
          <div class="tree-children ${isExpanded ? 'expanded' : 'collapsed'}" id="children-${item.id}" style="${!isExpanded ? 'display: none;' : ''}">
            ${childrenHtml}
          </div>
        ` : ''}
      </div>
    `
  }).join('')
  
  // Wrap the entire tree in a tree-display container
  return `<div class="tree-display">${treeItemsHtml}</div>`
}

// Generate interactive checklist
const generateChecklist = (content: string): string => {
  const lines = content.split('\n')
  const checklistItems = lines.map((line) => {
    const match = line.match(/^\s*([-*])\s*\[([ x])\]\s*(.+)$/)
    if (match) {
      const [, , checked, text] = match
      const isChecked = checked.toLowerCase() === 'x'
      const itemId = `checklist-${Math.random().toString(36).substr(2, 9)}`
      
      return `
        <div class="checklist-item">
          <input type="checkbox" id="${itemId}" ${isChecked ? 'checked' : ''} 
                 onchange="toggleChecklistItem(this)">
          <label for="${itemId}" class="checklist-label ${isChecked ? 'completed' : ''}">${escapeHtml(text)}</label>
        </div>
      `
    }
    return `<div class="checklist-text">${escapeHtml(line)}</div>`
  }).join('')

  return `
    <div class="interactive-checklist">
      <div class="checklist-header">
        <span class="checklist-title">Checklist</span>
        <button class="checklist-controls" onclick="toggleAllChecklist(this)">Toggle All</button>
      </div>
      <div class="checklist-content">
        ${checklistItems}
      </div>
    </div>
  `
}

// Generate config file display
const generateConfigFile = (content: string): string => {
  return `
    <div class="config-file-display">
      <div class="config-header">
        <span class="config-title">Configuration</span>
        <button class="copy-button" onclick="copyConfig(this)" title="Copy configuration">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="m5 15-4-4 4-4"></path>
          </svg>
        </button>
      </div>
      <pre class="config-content"><code>${escapeHtml(content)}</code></pre>
    </div>
  `
}

// Utility function to escape HTML
const escapeHtml = (text: string): string => {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

// Process markdown content
const processMarkdown = async () => {
  if (!props.content) {
    processedHtml.value = ''
    return
  }

  try {
    // Setup custom renderer
    const renderer = setupMarkedRenderer()
    marked.setOptions({ renderer })

    // Convert markdown to HTML
    const html = await marked.parse(props.content)
    processedHtml.value = html

    // Apply syntax highlighting after DOM update
    if (props.enableSyntaxHighlighting) {
      await nextTick()
      await applySyntaxHighlighting()
    }
  } catch {
    processedHtml.value = `<div class="error">Error processing markdown content</div>`
  }
}

// Apply syntax highlighting using Prism.js
const applySyntaxHighlighting = async () => {
  const Prism = await loadPrism()
  if (!Prism) return

  try {
    // Wait for DOM to be fully updated
    await nextTick()
    
    // Use a more specific selector and ensure the component is mounted
    const container = document.querySelector('.enhanced-markdown-renderer')
    if (!container) {
      return
    }
    
    const codeBlocks = container.querySelectorAll('pre code[class*="language-"]')
    codeBlocks.forEach((block) => {
      if (block && block instanceof HTMLElement) {
        Prism.highlightElement(block)
      }
    })
  } catch {
    // Silently handle syntax highlighting errors
  }
}

// Watch for content changes
watch(() => props.content, processMarkdown, { immediate: false })

// Mount lifecycle
onMounted(async () => {
  // Wait for DOM to be fully ready
  await nextTick()
  
  // Process initial content after DOM is ready
  if (props.content) {
    await processMarkdown()
  }
  
  // Add global functions for interactivity
  if (typeof window !== 'undefined') {
    // Copy code function
    ;(window as any).copyCode = (codeId: string) => {
      try {
        const codeElement = document.getElementById(codeId)
        if (codeElement) {
          navigator.clipboard.writeText(codeElement.textContent || '')
          // Show feedback
          const button = codeElement.closest('.code-block-container')?.querySelector('.copy-button')
          if (button) {
            const originalText = button.innerHTML
            button.innerHTML = '✓'
            setTimeout(() => {
              button.innerHTML = originalText
            }, 2000)
          }
        }
      } catch {
        // Silently handle copy errors
      }
    }

    // Toggle directory function
    ;(window as any).toggleDirectory = (directoryId: string) => {
      try {
        const childrenElement = document.getElementById(`children-${directoryId}`)
        const iconElement = document.querySelector(`[onclick="toggleDirectory('${directoryId}')"]`)
        
        if (childrenElement && iconElement) {
          const isExpanded = childrenElement.classList.contains('expanded')
          
          if (isExpanded) {
            // Collapse
            childrenElement.style.display = 'none'
            childrenElement.classList.remove('expanded')
            childrenElement.classList.add('collapsed')
            iconElement.classList.remove('expanded')
            iconElement.classList.add('collapsed')
            iconElement.textContent = '📁'
          } else {
            // Expand
            childrenElement.style.display = 'block'
            childrenElement.classList.remove('collapsed')
            childrenElement.classList.add('expanded')
            iconElement.classList.remove('collapsed')
            iconElement.classList.add('expanded')
            iconElement.textContent = '📂'
          }
        } else {
          // Elements not found, silently continue
        }
      } catch {
        // Silently handle toggle errors
      }
    }

    // Toggle checklist item
    ;(window as any).toggleChecklistItem = (checkbox: HTMLInputElement) => {
      try {
        const label = checkbox.nextElementSibling as HTMLElement
        if (label) {
          if (checkbox.checked) {
            label.classList.add('completed')
          } else {
            label.classList.remove('completed')
          }
        }
      } catch {
        // Silently handle checklist toggle errors
      }
    }

    // Toggle all checklist items
    ;(window as any).toggleAllChecklist = (button: HTMLElement) => {
      try {
        const checklist = button.closest('.interactive-checklist')
        const checkboxes = checklist?.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>
        
        if (checkboxes) {
          const allChecked = Array.from(checkboxes).every(cb => cb.checked)
          checkboxes.forEach(cb => {
            cb.checked = !allChecked
            ;(window as any).toggleChecklistItem(cb)
          })
        }
      } catch {
        // Silently handle bulk checklist toggle errors
      }
    }

    // Copy config
    ;(window as any).copyConfig = (button: HTMLElement) => {
      try {
        const configContent = button.closest('.config-file-display')?.querySelector('.config-content code')
        if (configContent) {
          navigator.clipboard.writeText(configContent.textContent || '')
          const originalText = button.innerHTML
          button.innerHTML = '✓'
          setTimeout(() => {
            button.innerHTML = originalText
          }, 2000)
        }
      } catch {
        // Silently handle config copy errors
      }
    }
  }
})
</script>

<style scoped>
.enhanced-markdown-renderer {
  width: 100%;
  max-width: none;
}

.markdown-content {
  color: rgb(55 65 81);
  line-height: 1.7;
}

.dark .markdown-content {
  color: rgb(209 213 219);
}

/* Code Block Styles - VS Dark Theme */
.code-block-container {
  margin: 1.5rem 0;
  border-radius: 8px;
  overflow: hidden;
  background: #000000;
  border: 1px solid #333333;
}

/* Prism VS Dark Plus Theme Styles */
.enhanced-markdown-renderer :deep(.token.comment),
.enhanced-markdown-renderer :deep(.token.prolog),
.enhanced-markdown-renderer :deep(.token.doctype),
.enhanced-markdown-renderer :deep(.token.cdata) {
  color: #6a9955;
}

.enhanced-markdown-renderer :deep(.token.punctuation) {
  color: #d4d4d4;
}

.enhanced-markdown-renderer :deep(.token.property),
.enhanced-markdown-renderer :deep(.token.tag),
.enhanced-markdown-renderer :deep(.token.boolean),
.enhanced-markdown-renderer :deep(.token.number),
.enhanced-markdown-renderer :deep(.token.constant),
.enhanced-markdown-renderer :deep(.token.symbol),
.enhanced-markdown-renderer :deep(.token.deleted) {
  color: #b5cea8;
}

.enhanced-markdown-renderer :deep(.token.selector),
.enhanced-markdown-renderer :deep(.token.attr-name),
.enhanced-markdown-renderer :deep(.token.string),
.enhanced-markdown-renderer :deep(.token.char),
.enhanced-markdown-renderer :deep(.token.builtin),
.enhanced-markdown-renderer :deep(.token.inserted) {
  color: #ce9178;
}

.enhanced-markdown-renderer :deep(.token.operator),
.enhanced-markdown-renderer :deep(.token.entity),
.enhanced-markdown-renderer :deep(.token.url),
.enhanced-markdown-renderer :deep(.language-css) .enhanced-markdown-renderer :deep(.token.string),
.enhanced-markdown-renderer :deep(.style) .enhanced-markdown-renderer :deep(.token.string) {
  color: #d4d4d4;
}

.enhanced-markdown-renderer :deep(.token.atrule),
.enhanced-markdown-renderer :deep(.token.attr-value),
.enhanced-markdown-renderer :deep(.token.keyword) {
  color: #569cd6;
}

.enhanced-markdown-renderer :deep(.token.function),
.enhanced-markdown-renderer :deep(.token.class-name) {
  color: #dcdcaa;
}

.enhanced-markdown-renderer :deep(.token.regex),
.enhanced-markdown-renderer :deep(.token.important),
.enhanced-markdown-renderer :deep(.token.variable) {
  color: #d16969;
}

.code-block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #111111;
  border-bottom: 1px solid #333333;
}

.code-language {
  font-size: 0.875rem;
  font-weight: 500;
  color: #cccccc;
  text-transform: uppercase;
}

.copy-button {
  background: transparent;
  border: 1px solid #555;
  color: #cccccc;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.2s ease;
}

.copy-button:hover {
  background: #333;
  border-color: #666;
}

.code-block-container pre {
  margin: 0;
  padding: 1rem;
  background: #000000;
  overflow-x: auto;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
}

.code-block-container code {
  color: #d4d4d4;
  background: transparent;
  padding: 0;
  border-radius: 0;
}

/* Tree Display Styles */
.tree-display {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  overflow-x: auto;
}

.dark .tree-display {
  background: #1f2937;
  border-color: #374151;
  color: #d1d5db;
}

.tree-item {
  margin: 0.25rem 0;
}

.tree-node {
  display: flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: default;
  transition: background-color 0.2s ease;
}

.tree-node.directory {
  cursor: default;
}

.tree-node.directory:hover {
  background: rgba(59, 130, 246, 0.1);
}

.tree-icon {
  margin-right: 0.5rem;
  font-size: 1rem;
}

.tree-icon.directory-icon {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.tree-icon.directory-icon:hover {
  transform: scale(1.1);
}

.tree-name {
  font-weight: 500;
  color: rgb(17 24 39);
}

.dark .tree-name {
  color: rgb(243 244 246);
}

.tree-description {
  margin-left: 0.5rem;
  font-size: 0.875rem;
  color: rgb(107 114 128);
  font-style: italic;
}

.dark .tree-description {
  color: rgb(156 163 175);
}

.tree-children {
  padding: 0.25rem 0;
  margin: 0.25rem 0;
}

.tree-children.collapsed {
  display: none !important;
}

.tree-children.expanded {
  display: block !important;
}

/* Interactive Checklist Styles */
.interactive-checklist {
  margin: 1.5rem 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  overflow: hidden;
}

.dark .interactive-checklist {
  background: #1f2937;
  border-color: #374151;
}

.checklist-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #e2e8f0;
  border-bottom: 1px solid #cbd5e1;
}

.dark .checklist-header {
  background: #374151;
  border-bottom-color: #4b5563;
}

.checklist-title {
  font-weight: 600;
  color: rgb(17 24 39);
}

.dark .checklist-title {
  color: rgb(243 244 246);
}

.checklist-controls {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.checklist-controls:hover {
  background: #2563eb;
}

.checklist-content {
  padding: 1rem;
}

.checklist-item {
  display: flex;
  align-items: center;
  margin: 0.5rem 0;
}

.checklist-item input[type="checkbox"] {
  margin-right: 0.75rem;
  width: 1rem;
  height: 1rem;
  accent-color: #3b82f6;
}

.checklist-label {
  flex: 1;
  color: rgb(55 65 81);
  transition: all 0.2s ease;
}

.dark .checklist-label {
  color: rgb(209 213 219);
}

.checklist-label.completed {
  text-decoration: line-through;
  opacity: 0.7;
}

.checklist-text {
  color: rgb(75 85 99);
  margin: 0.25rem 0;
}

.dark .checklist-text {
  color: rgb(156 163 175);
}

/* Config File Display Styles */
.config-file-display {
  margin: 1.5rem 0;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  background: #fffbeb;
  overflow: hidden;
}

.dark .config-file-display {
  background: #451a03;
  border-color: #d97706;
}

.config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #fef3c7;
  border-bottom: 1px solid #fbbf24;
}

.dark .config-header {
  background: #92400e;
  border-bottom-color: #d97706;
}

.config-title {
  font-weight: 600;
  color: #92400e;
}

.dark .config-title {
  color: #fbbf24;
}

.config-content {
  margin: 0;
  padding: 1rem;
  background: #fffbeb;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #451a03;
}

.dark .config-content {
  background: #451a03;
  color: #fbbf24;
}

/* Smart Blockquote Styles */
.smart-blockquote {
  margin: 1.5rem 0;
  padding: 1rem 1.5rem;
  border-left: 4px solid #3b82f6;
  background: #eff6ff;
  border-radius: 0 8px 8px 0;
  font-style: italic;
}

.dark .smart-blockquote {
  background: #1e3a8a;
  border-left-color: #60a5fa;
  color: #dbeafe;
}

/* Error Styles */
.error {
  padding: 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  margin: 1rem 0;
}

.dark .error {
  background: #7f1d1d;
  border-color: #dc2626;
  color: #fecaca;
}

/* Responsive Design */
@media (max-width: 768px) {
  .code-block-header,
  .checklist-header,
  .config-header {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  
  .tree-item {
    font-size: 0.875rem;
  }
}
</style>
